import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useDocumentMeta from '../useDocumentMeta';
import LeadMagnetModal from '../LeadMagnetModal';
import Greeting from '../Greeting';
import TestYourselfPrompt from '../TestYourselfPrompt';
import { track } from '../analytics';

// `id` is the GA4 parameter value; see the note on TIMELINE_SHORTCUTS below.
const CHAPTERS = [
    {
        id: 'british_history',
        to: '/study-guide/british-history',
        emoji: '🛡️',
        title: 'British History',
        desc: 'Monarchs, key dates, inventions, and the people the exam loves to mix up: from William the Conqueror to the present.',
        available: true,
    },
    {
        id: 'government_and_law',
        to: '/study-guide/government-and-law',
        emoji: '⚖️',
        title: 'Government & Law',
        desc: 'Elections, Parliament, the justice system, and how the UK is governed.',
        available: true,
    },
    {
        id: 'culture_and_traditions',
        to: '/study-guide/culture-and-traditions',
        emoji: '🎉',
        title: 'Culture & Traditions',
        desc: 'Patron saints, national festivals, sports milestones, and everyday British customs.',
        available: true,
    },
    {
        id: 'modern_britain',
        to: '/study-guide/modern-britain',
        emoji: '🌐',
        title: 'Modern Britain & Society',
        desc: 'Human rights, citizenship, taxation, and the UK\'s role in the world today.',
        available: true,
    },
];

// Direct links to the visual timelines (added 2026-09-15).
//
// These are the site's most distinctive content and they were two taps down:
// /study-guide -> British History -> the timeline. GA4 for 1-14 Sep showed
// monarchs-timeline 34 users, key-dates 10 and inventions-timeline 7, against
// 417 landing on /study-guide itself. Nothing on the landing page said that
// "British History" contained visual timelines, so nobody looked.
//
// Titles and emoji mirror the TOOLS list in BritishHistory.js. If one changes,
// change both: two different names for the same page reads as two pages.
// `id` is the GA4 event parameter value, kept separate from `label` so a copy
// change to the visible text never silently splits the reporting history.
const TIMELINE_SHORTCUTS = [
    { to: '/study-guide/monarchs-timeline', emoji: '👑', label: 'Monarchs', id: 'monarchs' },
    { to: '/study-guide/key-dates', emoji: '📅', label: 'Key dates', id: 'key_dates' },
    { to: '/study-guide/inventions-timeline', emoji: '💡', label: 'Inventions', id: 'inventions' },
    { to: '/study-guide/people-matrix', emoji: '🧑‍🎓', label: "Who's who", id: 'people_matrix' },
];

// The immigration guides carry roughly 60% of the site's search impressions but
// sit at positions 52-76, and until this list existed nothing on the site linked
// into them except a single /ilr-guide mention below. They were reachable only
// via sitemap.xml, so no internal link equity reached them and Google had no
// on-site signal about which were important or how they related to each other.
//
// Grouped by the applicant's actual journey rather than alphabetically, so the
// anchor text and the grouping both describe the target to a crawler. Slugs are
// verified against guideBySlug in pages/immigrationGuides/index.js — a typo here
// is a silent 404, since these are plain strings with no build-time check.
const GUIDE_GROUPS = [
    {
        heading: 'Settlement (ILR)',
        guides: [
            { to: '/ilr-guide', title: 'Indefinite Leave to Remain: complete guide' },
            { to: '/skilled-worker-ilr', title: 'ILR on a Skilled Worker visa' },
            { to: '/spouse-visa-ilr', title: 'ILR on a spouse or partner visa' },
            { to: '/long-residence-ilr', title: 'ILR after 10 years long residence' },
            { to: '/global-talent-ilr', title: 'ILR on a Global Talent visa' },
            { to: '/skilled-worker-redundancy', title: 'Redundancy on a Skilled Worker visa' },
        ],
    },
    {
        heading: 'Citizenship',
        guides: [
            { to: '/british-citizenship-guide', title: 'Applying for British citizenship' },
            { to: '/citizenship-ceremony', title: 'The citizenship ceremony' },
            { to: '/british-passport-application', title: 'Applying for a British passport' },
            { to: '/dual-citizenship', title: 'Dual citizenship and the UK' },
        ],
    },
    {
        heading: 'Requirements and process',
        guides: [
            { to: '/life-in-uk-requirement', title: 'The Life in the UK test requirement' },
            { to: '/english-requirement', title: 'The English language requirement' },
            { to: '/ukvcas-appointment', title: 'Your biometric appointment' },
            { to: '/evisa-explained', title: 'eVisas and the move from BRP cards' },
        ],
    },
];

function ChapterCard({ chapter }) {
    const content = (
        <>
            <div className="text-3xl mb-3">{chapter.emoji}</div>
            <h3 className="font-bold text-ink-900 mb-1">{chapter.title}</h3>
            <p className="text-sm text-ink-600 leading-relaxed">{chapter.desc}</p>
            {!chapter.available && (
                <span className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-action-text">Coming soon</span>
            )}
        </>
    );

    const baseClass = "block rounded-2xl border p-6 shadow-lift-1 transition bg-ink-50 border-ink-200";

    if (!chapter.available) {
        return <div className={`${baseClass} opacity-60 cursor-not-allowed`}>{content}</div>;
    }

    return (
        <Link
            to={chapter.to}
            onClick={() => track('study_guide_chapter_click', { chapter: chapter.id })}
            className={`${baseClass} hover:border-action-500 hover:shadow-lift-2`}
        >
            {content}
        </Link>
    );
}

const SITE_URL = "https://lifeinukcoach.co.uk";

const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Study Guide", item: `${SITE_URL}/study-guide/` },
    ],
};

export default function StudyGuideIndex() {
    useDocumentMeta({
        title: "Study Guide: Life in the UK Test 2026",
        description: "Free visual study guides for the Life in the UK Test: British history, government & law, culture, and modern society, all sourced from the official handbook.",
        path: "/study-guide",
        jsonLd: breadcrumbSchema,
    });

    const [showLeadMagnet, setShowLeadMagnet] = useState(false);

    return (
        <div className="min-h-screen bg-ink-100 py-6 sm:py-12 px-4 sm:px-6 lg:px-8 pb-28 md:pb-12">
            <div className="max-w-3xl mx-auto bg-ink-50 rounded-2xl shadow-lift-1 border border-ink-200 p-5 sm:p-8 md:p-12">
                {/* Wording changed 2026-09-15: "Back to Flashcards" implies the
                    visitor came from there, but this is a landing page for most
                    people — they arrive from a shared link, not from the home page. */}
                <Link to="/" className="text-action-text hover:text-action-text font-semibold text-sm mb-4 inline-flex items-center min-h-tap">
                    ← All flashcards
                </Link>
                {/* This page is the link shared into Facebook and WhatsApp
                    groups, so it is a landing page for most visitors rather
                    than an interior page. The greeting is the first thing they
                    read: it cycles through the languages most spoken by people
                    sitting this test, which is the audience arriving here. */}
                <p
                    className="text-action-text font-display font-bold text-lg leading-none mb-2 flex items-center"
                    style={{ minHeight: '1.5rem' }}
                >
                    <Greeting />
                </p>
                <h1 className="text-3xl font-extrabold text-ink-900 mb-3">
                    Life in the UK Study Guide
                </h1>
                <p className="text-ink-600 mb-6 leading-relaxed u-measure">
                    Free visual study guides covering every chapter of the official Life in the UK
                    Test handbook: timelines, cheat sheets and fact cards. Not sure where to start?
                    Take a mock exam first and find out what you already know.
                </p>

                {/* Primary action, added 2026-09-15.
                    This page is the site's #1 landing page (417 users in the first two weeks of
                    September, vs 300 on the homepage) because it is the link shared into Facebook
                    groups. It held attention for 13.7 seconds per user, against 383s on
                    /mock-exams — people arrived, saw twelve equal-weight chapters with no
                    suggested starting point, and left. The exam prompt existed but sat ~1,500px
                    down the page, below all twelve chapter cards and the email capture.

                    So the same CTA the homepage leads with is repeated here, above the fold. The
                    chapter grid is still directly below for anyone who does want to browse. */}
                <div className="mb-8">
                    <Link
                        to="/mock-exams"
                        onClick={() => track('test_yourself_click', { topic: 'study_guide_hero', choice: 'full' })}
                        className="w-full inline-flex items-center justify-center gap-2 bg-action-600 hover:bg-action-700 text-white font-bold text-base px-6 py-4 min-h-tap rounded-xl transition shadow-lift-2"
                    >
                        Start a Free Mock Exam
                    </Link>
                    <p className="mt-2 text-center text-xs text-ink-500">
                        Free &middot; No sign-up &middot; No credit card required
                    </p>
                </div>

                <p className="text-sm font-semibold text-ink-700 mb-3">
                    Or browse the four chapters
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    {CHAPTERS.map(chapter => <ChapterCard key={chapter.title} chapter={chapter} />)}
                </div>

                {/* Jump straight to a timeline. Sits below the four chapters
                    rather than inside the grid so the chapters stay one
                    coherent set, but above the email capture so it is reached
                    before the page asks for anything. Deliberately small chips,
                    not cards: these are shortcuts into British History, not a
                    fifth chapter competing with the four above. */}
                <div className="mb-8">
                    <p className="text-sm font-semibold text-ink-700 mb-3">
                        Or jump straight to a visual timeline
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {TIMELINE_SHORTCUTS.map(item => (
                            <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => track('timeline_shortcut_click', { timeline: item.id })}
                                className="inline-flex items-center gap-2 px-4 min-h-tap rounded-xl bg-ink-100 border border-ink-200 text-sm font-semibold text-ink-800 hover:border-action-500 hover:text-action-text transition shadow-lift-1"
                            >
                                <span aria-hidden="true">{item.emoji}</span>
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Email capture. This is the page's own conversion goal, so it
                    is given a full-width tap target rather than a small centred
                    button, and names what arrives rather than the action. */}
                <div className="bg-action-50 border border-action-100 rounded-2xl p-6 mb-8">
                    <p className="text-ink-900 font-display font-bold text-lg mb-1">Want it on paper?</p>
                    <p className="text-sm text-ink-600 mb-4 u-measure">
                        A free 1-page Key Dates &amp; Patron Saints cheat sheet, handbook-verified and
                        print-ready, straight to your inbox.
                    </p>
                    <button
                        onClick={() => { track('lead_magnet_open'); setShowLeadMagnet(true); }}
                        className="w-full sm:w-auto inline-flex items-center justify-center bg-action-600 hover:bg-action-700 text-white font-bold px-6 min-h-tap rounded-xl transition shadow-lift-2"
                    >
                        Get the free cheat sheet
                    </button>
                </div>

                <TestYourselfPrompt topic="study_guide_index" />

                <p className="text-sm text-ink-500 mt-6">
                    Already have ILR? See what's next on your <Link to="/ilr-guide" className="text-action-text font-semibold hover:underline">path to British citizenship</Link>.
                </p>
            </div>

            {/* Entry point into the immigration guide cluster. See GUIDE_GROUPS
                above for why this exists. */}
            <div className="max-w-4xl mx-auto mt-16">
                <h2 className="text-2xl font-bold text-ink-900 mb-2 text-center">
                    Visa, Settlement &amp; Citizenship Guides
                </h2>
                <p className="text-sm text-ink-600 mb-8 text-center max-w-2xl mx-auto">
                    The Life in the UK test is one step in a longer application. These guides
                    cover the rest of the route, from settlement to citizenship.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {GUIDE_GROUPS.map((group) => (
                        <div
                            key={group.heading}
                            className="bg-ink-50 rounded-2xl border border-ink-200 p-5 shadow-lift-1"
                        >
                            <h3 className="text-xs font-bold text-action-text uppercase tracking-wider mb-3">
                                {group.heading}
                            </h3>
                            <ul className="space-y-2.5">
                                {group.guides.map((guide) => (
                                    <li key={guide.to}>
                                        <Link
                                            to={guide.to}
                                            className="text-sm text-ink-700 hover:text-action-text hover:underline transition leading-snug flex items-center min-h-tap"
                                        >
                                            {guide.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            <LeadMagnetModal isOpen={showLeadMagnet} onClose={() => setShowLeadMagnet(false)} />
        </div>
    );
}
